/**
 * Failed Step
 *
 * Displayed when the onboarding flow fails (FlowStatus.FAILED).
 * Shows an error message and instructs the user to refresh the page.
 */

import React from 'react';
import { AlertCircleIcon } from '@newcore/icons';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { hasErrorMessage } from '@/features/onboarding/utils/has-error-message';

interface FailedStepProps {
  error: string | null;
  onClearError: () => void;
}

/**
 * Failed Step - Shown when backend returns FlowStatus.FAILED
 *
 * This can happen when:
 * - Factor validation fails (wrong OTP, failed TOTP setup, etc.)
 * - Onboarding completion fails (policy not satisfied)
 * - Session timeout or other backend errors
 */
export const FailedStep: React.FC<FailedStepProps> = ({
  error,
  onClearError,
}) => {
  return (
    <div className='flex flex-col items-center text-center space-y-6 py-4'>
      <Onboarding.Icon className='h-20 w-20 bg-error-solid/10 text-error-primary [&>svg]:h-10 [&>svg]:w-10'>
        <AlertCircleIcon />
      </Onboarding.Icon>

      <div className='space-y-2' data-testid='onboarding-setup-failed-title'>
        <Onboarding.Title className='text-error-primary'>
          Setup Failed
        </Onboarding.Title>
        <Onboarding.Description className='max-w-sm'>
          We couldn&apos;t complete your account setup. Please refresh the page
          to try again. If the problem persists, contact your administrator.
        </Onboarding.Description>
      </div>

      {hasErrorMessage(error) && (
        <Onboarding.Error
          error={error}
          onDismiss={onClearError}
          className='w-full max-w-sm'
        />
      )}
    </div>
  );
};
